import { useEffect, useState } from 'react'
import { ServerApiClient, type ServerWorkbook } from '../../data/ServerApiClient'
import { CfoBrandMark, navigateTo } from '../cfo-site/CfoSiteShared'
import { FgHelp } from '../help/FgHelp'
import { companyFromSearch, connectApi, type ActivityItem } from './connectApi'
import './ConnectPage.css'

export type AiActivityDeps = {
  activity: typeof connectApi.activity
  listWorkbooks: () => Promise<ServerWorkbook[]>
}
const DEFAULT_DEPS: AiActivityDeps = { activity: connectApi.activity, listWorkbooks: () => new ServerApiClient().listWorkbooks() }

const VIA_LABELS: Record<string, string> = {
  mcp: 'Claude / ChatGPT',
  remote_mcp: 'Claude / ChatGPT',
  sidebar: 'Excel sidebar',
  excel_sidebar: 'Excel sidebar',
  web: 'Web',
  rest: 'Web',
  workbook_runtime: 'Excel workbook',
}


/** "What the AI did": every AI call and Excel change for one company, newest first. */
export function AiActivityPage({ deps = DEFAULT_DEPS, initialCompany = typeof window === 'undefined' ? '' : companyFromSearch(window.location.search) }: { deps?: AiActivityDeps; initialCompany?: string }) {
  const [workbooks, setWorkbooks] = useState<ServerWorkbook[] | null>(null)
  const [company, setCompany] = useState(initialCompany)
  const [items, setItems] = useState<ActivityItem[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => { deps.listWorkbooks().then(setWorkbooks, () => setWorkbooks([])) }, [deps])
  useEffect(() => {
    if (!company) { setItems(null); return }
    let cancelled = false
    setError(null)
    setItems(null)
    deps.activity(company).then(next => { if (!cancelled) setItems(next) }, () => { if (!cancelled) setError('The activity log could not be loaded for this company.') })
    return () => { cancelled = true }
  }, [company, deps])

  const choose = (id: string) => {
    setCompany(id)
    window.history.replaceState({}, '', id ? `/ai-activity?company=${encodeURIComponent(id)}` : '/ai-activity')
  }

  return <div className="fg-connect">
    <nav className="fg-connect-nav">
      <a href="/" onClick={event => { event.preventDefault(); navigateTo('/') }}><CfoBrandMark sub="What the AI did" /></a>
      <span><a href="/connect">Connect</a><a href="/support">Support</a></span>
    </nav>
    <main className="fg-connect-main">
      <header className="fg-connect-hero">
        <h1>What the AI did<FgHelp id="connect.ai_activity" /></h1>
        <p>Every request an AI app or the Excel sidebar made for a company, and every change it asked to make in Excel, with who approved it.</p>
      </header>
      <section className="fg-connect-card">
        <label className="fg-connect-inline">Company
          <select value={company} onChange={event => choose(event.target.value)}>
            <option value="">Choose a company</option>
            {(workbooks ?? []).map(workbook => <option key={workbook.id} value={workbook.id}>{workbook.name}</option>)}
          </select>
        </label>
        {error && <p className="fg-connect-error" role="alert">{error}</p>}
        {company && !items && !error && <p className="fg-connect-muted" role="status">Loading…</p>}
        {items?.length === 0 && <p className="fg-connect-muted">No AI activity for this company yet.</p>}
        {items && items.length > 0 && <ol className="fg-activity">
          {items.map(item => <li key={`${item.kind}:${item.id}`} data-kind={item.kind}>
            <time dateTime={item.at}>{new Date(item.at).toLocaleString()}</time>
            <div>
              <div><strong>{item.summary}</strong></div>
              <div className="fg-connect-muted">
                {VIA_LABELS[item.via] ?? item.via}{item.client_name ? ` (${item.client_name})` : ''}{item.who ? ` · ${item.who}` : ''} · <code>{item.name}</code> · <span className="fg-activity-status" data-status={item.status}>{item.status.replaceAll('_', ' ')}</span>{item.error_code ? ` (${item.error_code})` : ''}
              </div>
            </div>
          </li>)}
        </ol>}
      </section>
    </main>
  </div>
}

export default AiActivityPage
